import type { FunctionComponent } from 'react';
import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import type { VerificationCompleteStatus } from '@endeavour/verification-integration';
import { useClient } from 'urql';

import { config } from '../../../../config/config';
import type {
    ILaunchVerificationProviderMutation,
    ILaunchVerificationProviderMutationVariables,
} from '../../graphql/verification-provider.graphql';
import { LaunchVerificationProviderDocument } from '../../graphql/verification-provider.graphql';
import { useVerificationProvider } from '../../util/verification-context';

export interface IUploadIntegrationProps {
    className?: string;
    providerConfig: unknown;
    onDocumentUploaded: (status: VerificationCompleteStatus, data: unknown) => void;
}

export const UploadIntegration: FunctionComponent<IUploadIntegrationProps> = ({
    className,
    providerConfig,
    onDocumentUploaded,
}) => {
    const ref = useRef(null);
    const handleDocumentUploaded = useRef(onDocumentUploaded);
    const userId = useAuth().user?.profile.user_profile_id as string;

    const provider = useVerificationProvider();
    const client = useClient();

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const integration = provider.initialize({
            userProfileId: userId,
            tenantId: config.TENANT_ID,
            container: ref.current,
            providerConfig,
            async getSession() {
                const { data } = await client
                    .mutation<
                        ILaunchVerificationProviderMutation,
                        ILaunchVerificationProviderMutationVariables
                    >(LaunchVerificationProviderDocument, {
                        userProfileId: userId,
                        xTenantID: config.TENANT_ID,
                        provider: 'onfido',
                    })
                    .toPromise();

                return {
                    sessionId: data?.launchVerificationProvider?.token ?? '',
                };
            },
        });

        const destructors: Array<() => void> = [];

        integration.then((result) => {
            if (result.success) {
                destructors.push(result.onProcessComplete(handleDocumentUploaded.current));
            }
        });

        return () => {
            (async () => {
                const response = await integration;
                destructors.forEach((destroy) => {
                    destroy();
                });
                if (response.success) {
                    response.destroy();
                }
            })();
        };
    }, [client, provider, providerConfig, userId]);

    return <div className={className} ref={ref}></div>;
};
